import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  Check,
  CircleDashed,
  CircleParking,
  Globe,
  LayoutDashboard,
  Route,
  ScrollText,
  Send,
  Smartphone,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { OBJECT_AREA_GROUPS, type ObjectArea, type ObjectAreaId } from './objectAreas';
import type { AreaHint } from './objectAreaStatus';

export const OBJECT_AREA_ICONS: Record<ObjectAreaId, LucideIcon> = {
  overview: LayoutDashboard,
  stammdaten: Building2,
  anfahrt: Route,
  parken: CircleParking,
  ressourcen: CalendarClock,
  hausordnung: ScrollText,
  nachrichten: Send,
  gastportal: Smartphone,
  veroeffentlichung: Globe,
};

interface Props {
  groupId: string;
  areas: ObjectArea[];
  activeId?: ObjectAreaId;
  hints: Partial<Record<ObjectAreaId, AreaHint>>;
  onNavigate?: () => void;
}

/** Gruppierte Bereichsliste — dieselbe Form wie die Einstellungen, 44 px je Zeile (§9). */
export function ObjectAreaNav({ groupId, areas, activeId, hints, onNavigate }: Props) {
  return (
    <nav aria-label="Objekt-Bereiche" className="flex flex-col gap-5">
      {OBJECT_AREA_GROUPS.map((group) => {
        const items = areas.filter((area) => area.group === group.id);
        if (items.length === 0) return null;
        return (
          <div key={group.id}>
            <div className="text-ink-faint px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em]">
              {group.label}
            </div>
            <ul className="flex flex-col gap-0.5">
              {items.map((area) => {
                const active = area.id === activeId;
                const Icon = OBJECT_AREA_ICONS[area.id];
                return (
                  <li key={area.id}>
                    <Link
                      to={area.to}
                      params={{ id: groupId }}
                      onClick={onNavigate}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'focus-visible:ring-sage flex min-h-11 items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 motion-reduce:transition-none',
                        active
                          ? 'bg-navy text-paper'
                          : 'text-ink-soft hover:bg-card hover:text-ink',
                      )}
                    >
                      <Icon size={16} strokeWidth={2} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{area.label}</span>
                      <AreaHintMark hint={hints[area.id]} active={active} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

export function AreaHintMark({ hint, active }: { hint?: AreaHint; active: boolean }) {
  if (!hint) return null;
  if (hint.tone === 'ok') {
    return (
      <span className={active ? 'text-sage-on-dark' : 'text-sage'}>
        <Check size={14} strokeWidth={2.5} aria-hidden />
        <span className="sr-only">{hint.label}</span>
      </span>
    );
  }
  const Icon =
    hint.tone === 'open' ? CircleDashed : hint.tone === 'attention' ? AlertTriangle : null;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 text-[11.5px] font-medium',
        active ? 'text-paper/75' : 'text-ink-soft',
      )}
    >
      {Icon && (
        <Icon
          size={12}
          strokeWidth={2}
          aria-hidden
          className={
            hint.tone === 'attention' ? (active ? 'text-amber-on-dark' : 'text-amber') : ''
          }
        />
      )}
      {/* Kurz fürs Auge, vollständig fürs Ohr: „1/2" läse sich vor wie ein Bruch. */}
      <span aria-hidden>{hint.text}</span>
      <span className="sr-only">{hint.label}</span>
    </span>
  );
}
